<template>
    <v-layout row justify-center>
        <v-dialog
            v-model="dialog"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
            scrollable
        >
            <v-card tile>
                <v-toolbar
                    card
                    dark
                    class="teal"
                    :class="{'darken-2': !this.$vuetify.dark, 'darken-4': this.$vuetify.dark}"
                >
                    <v-toolbar-title>{{ $t("pattern3dView") }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="dialog = false">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>

                <canvas id="c"></canvas>

                <button type="button" class="action-button" id="screenshot"><v-icon>mdi-camera</v-icon> <span>{{ $t("screenshot") }}</span></button>
                <button type="button" class="action-button" id="startPosition"
                    @click="toggleStart = !toggleStart"
                    @touchstart="toggleStart = !toggleStart"
                >
                    <v-icon>mdi-rotate-orbit</v-icon>
                    <span>{{ $t("rotateToStartPosition") }}</span>
                </button>
                <button type="button" class="action-button" id="logTransparency"
                    @click="showLog = !showLog"
                    @touchstart="showLog = !showLog"
                >
                    <v-icon>mdi-transition</v-icon>
                    <span>{{ $t("toggleLog") }}</span>
                </button>
                <button type="button" class="action-button" id="cutsShow"
                    @click="showCuts = !showCuts"
                    @touchstart="showCuts = !showCuts"
                >
                    <v-icon>mdi-flip-horizontal</v-icon>
                    <span>{{ $t("toggleCuts") }}</span>
                </button>
                <button type="button" class="action-button" id="curvatureMode"
                    @click="toggleCurvature = !toggleCurvature"
                    @touchstart="toggleCurvature = !toggleCurvature"
                >
                    <v-icon>mdi-current-ac</v-icon>
                    <span>{{ $t("toggleCurvatureMode") }}</span>
                </button>

                <table class="info-table">
                    <tbody>
                        <tr>
                            <td><b>{{ $t("speciesAlt") }}:</b></td>
                            <td>{{ logInfo.species !== null ? $t(logInfo.species) : "" }}</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("price") }}:</b></td>
                            <td>{{ totalPrice }}</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("output") }}:</b></td>
                            <td>{{ output.toFixed(2) }}%</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("logVolume") }}:</b></td>
                            <td>{{ logInfo.volume }} {{ $t("m")}}<sup>3</sup></td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("diameterTop") }}:</b></td>
                            <td>{{ logInfo.dTop }} {{ $t("mm")}}</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("diameterBottom") }}:</b></td>
                            <td>{{ logInfo.dBottom }} {{ $t("mm")}}</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("length") }}:</b></td>
                            <td>{{ logInfo.length.toFixed(2) }} {{ $t("m")}}</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("curvature") }}:</b></td>
                            <td>{{ logInfo.curvature.toFixed(2) }}%</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("ellipseRatio") }}:</b></td>
                            <td>{{ logInfo.ellipseRatio.toFixed(2) }}%</td>
                        </tr>
                        <tr>
                            <td><b>{{ $t("calculated") }}:</b></td>
                            <td>{{ calcTimestamp }}</td>
                        </tr>
                    </tbody>
                </table>
            </v-card>
        </v-dialog>
        <v-alert
            :value="alert !== ''"
            :type="alertType"
            transition="scale-transition"
            class="bottom-alert"
        >
            {{ alert }}
        </v-alert>
    </v-layout>
</template>

<script>
//import * as THREE from "three";
import {
    BoxGeometry,
    //BufferGeometry,
    CylinderGeometry,
    DoubleSide,
    EdgesGeometry,
    //Float32BufferAttribute,
    HemisphereLight,
    LineBasicMaterial,
    LineSegments,
    Mesh,
    MeshBasicMaterial,
    MeshLambertMaterial,
    MeshStandardMaterial,
    PerspectiveCamera,
    RingGeometry,
    Scene,
    Vector3,
    WebGLRenderer,
} from "three"; //"three/src/Three";
//import OrbitControls from "orbit-controls-es6";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Text }          from "troika-three-text";

export default {
    name: "ThreeDPatternView",

    props: ["pattern"],

    data() {
        return {
            dialog:          false,
            alert:           "",
            alertType:       "info",
            calcTimestamp:   "",
            output:          0,
            totalPrice:      0,
            logInfo:         {
                curvCenter:     0.5,
                curvature:      0,
                dBottom:        0,
                dTop:           0,
                ellipseRatio:   0,
                length:         0,
                rotationAngle:  0,
                species:        "",
                volume:         ""
            },
            showLog:         true,
            showCuts:        true,
            toggleStart:     true,
            toggleCurvature: true,
            scene:           null,
            renderer:        null,
            camera:          null,
            controls:        null,
            models:          [],
            cameraPosition:  {
                x: 0,
                y: 0,
                z: 0,
            },
            initialPosition: {
                camera:   null,
                controls: null
            }

        }
    },

    watch: {
        alert: function (val) {
            if (val) {
                setTimeout(() => this.alert = "", 1500);
            }
        },

        dialog: function (val) {
            if (val == true) {
                this.initialPosition.camera   = new Vector3(0, 0, -(this.logInfo.length*1000/4 + this.logInfo.dTop*1.5));
                this.initialPosition.controls = new Vector3(0, 0, 0);
                //setTimeout(this.initThree(), 1500);
                this.initThree();
            } else {
                this.saveCameraPosition();
                this.cleanup();
                this.initialPosition.camera   = undefined;
                this.initialPosition.controls = undefined;
            }
        },

        showLog: function () {
            this.saveCameraPosition();
            this.cleanup();
            this.initThree();
        },

        showCuts: function () {
            this.saveCameraPosition();
            this.cleanup();
            this.initThree();
        },

        toggleStart: function () {
            let alpha = 0;
            let timerId = setInterval(() => {
                this.camera.position.lerp(this.initialPosition.camera,   alpha);
                this.controls.target.lerp(this.initialPosition.controls, alpha);

                alpha += 0.05;

                this.controls.update();

                if (alpha >= 1) { clearInterval(timerId); }
            }, 25);
        },

        toggleCurvature: function () {
            this.saveCameraPosition();
            this.cleanup();
            this.initThree();
        }

    },

    methods: {
        /**
         * Добавление границ объектам
         *
         * @param {object} object Объект к которому добавляются границы
         */
        addEdges (object) {
            const edgeGeo = new EdgesGeometry (object.geometry);
            const edgeMat = new LineBasicMaterial({ color: 0x6e2c10, linewidth: 10 });
            const edges   = new LineSegments (edgeGeo, edgeMat);

            object.add(edges);
        },

        /**
         * Очистка объектов
         */
        cleanup () {
            while(this.models.length > 0) {
                this.models[this.models.length - 1].geometry.dispose();
                this.models[this.models.length - 1].material.dispose();
                //this.models[this.models.length - 1].texture.dispose();
                this.scene.remove(this.models[this.models.length - 1]);
                this.models.pop();
            }

            this.models = undefined;
            this.models = [];
            this.scene  = undefined;
            //this.renderer  = undefined;
            //this.camera  = undefined;
            //this.controls  = undefined;
        },

        /**
         * Расчёт шрифта для надписей с размерами сечений
         *
         * @param {object} t Высота доски
         * @param {object} w Ширина доски
         */
        calcFontSize (t, w) {
            let font = 0;

            if (t == w) {
                if (t < 50) { font = t * w * 0.003 }
                else if (t < 100) { font = t * w * 0.0015 }
                else if (t < 150) { font = t * w * 0.0009 }
                else if (t < 200) { font = t * w * 0.0006 }
                else if (t < 250) { font = t * w * 0.0005 }
                else if (t < 300) { font = t * w * 0.0004 }
                else if (t < 400) { font = t * w * 0.0003 }
                else { font = t * w * 0.0002 }
            } else {
                if (t < 20) { font = t * w * 0.003 }
                else if (t < 70) { font = t * w * 0.002 }
                else if (t < 120) { font = t * w * 0.001 }
                else { font = t * w * 0.0005 }
            }

            return font;
        },

        /**
         * Отрисовка боковых досок
         *
         * @param {object} scene   Сцена
         * @param {object} pattern Параметры постава
         * @param {number} order   Порядок досок: 1 - левые/правые, 2 - верхние/нижние
         * @param {object} log     Параметры бревна
         * @param {number} sq      Общая толщина центральных досок
         */
        drawSideBoard (scene, pattern, order, cenOrder, log, sq) {
            let geometryBoard;
            let size, sizeT;
            const materialBoard  = new MeshLambertMaterial({ color: 0xebc81a });
            const materialBoardCut = new MeshLambertMaterial({ color: 0xe88358 });
            const materialBoardWane = new MeshLambertMaterial({ color: 0xFFECB3 });
            let col = pattern.items.length;
            let usedArea1 = 0;
            let usedArea2 = 0;
            let sqW = 0;
            //Отрисовка первой, левой половины
            for (let i = cenOrder-1; i >= 0; --i) {
                if (pattern.items[i].hasOwnProperty("items")) {
                    sqW = 0;
                    for (let j = 0; j < pattern.items[i].items.length; j++) {
                        sqW += pattern.items[i].items[j].ws;
                    }
                    sqW += (pattern.items[i].items.length - 1) * pattern.items[i].kerf;
                    sqW /= 2;
                    // let containers = pattern.items[i].items.length;

                    for (let j = 0; j < pattern.items[i].items.length; j++) {
                        let boardWane = pattern.items[i].items[j].waneLen ? pattern.items[i].items[j].waneLen*1000 : 0;
                        let boardLen = boardWane && boardWane > 0 ? pattern.items[i].items[j].len*1000 - boardWane : pattern.items[i].items[j].len*1000;
                        let logLen = log.length*1000;

                        geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardLen/2);

                        this.models.push(new Mesh(geometryBoard, materialBoard));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        if (order == 1) {
                            board.position.x = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                            board.position.y = sqW - pattern.items[i].items[j].ws/2;
                        } else {
                            board.position.y = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                            board.rotation.z = Math.PI /2;
                            board.position.x = sqW - pattern.items[i].items[j].ws/2;
                        }

                        board.position.z = logLen/4 - boardLen/4;

                        this.addEdges(board);

                        if (boardWane && boardWane > 0) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardWane));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);

                            if (order == 1) {
                                board.position.x = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                                board.position.y = sqW - pattern.items[i].items[j].ws/2;
                            } else {
                                board.position.y = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                                board.rotation.z = Math.PI /2;
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            this.addEdges(board);
                        }

                        if (boardLen < logLen && this.showCuts) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, logLen/2 - boardLen/2 - boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardCut));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);

                            if (order == 1) {
                                board.position.x = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                                board.position.y = sqW - pattern.items[i].items[j].ws/2;
                            } else {
                                board.position.y = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                                board.rotation.z = Math.PI /2;
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            this.addEdges(board);
                        }

                        size = new Text();

                        scene.add(size);

                        size.maxWidth = pattern.items[i].items[j].wn;
                        size.text = `${pattern.items[i].items[j].tn} x ${pattern.items[i].items[j].wn} x ${pattern.items[i].items[j].len.toFixed(1)}`;
                        if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].items[j].waneLen.toFixed(1) + ")";}
                        size.fontSize = this.calcFontSize(pattern.items[i].items[j].ts, pattern.items[i].items[j].ws);
                        size.color    = 0x000000;
                        size.anchorX  = "50%";
                        size.anchorY  = "50%";

                        size.position.z = logLen/4 + 1;

                        if (order == 1) {
                            size.position.x = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                            size.rotation.z = Math.PI /2;
                            size.position.y = sqW - pattern.items[i].items[j].ws/2;
                        } else {
                            size.position.y = sq + usedArea1 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf;
                            size.position.x = sqW - pattern.items[i].items[j].ws/2;
                        }

                        sizeT = new Text();

                        sizeT.copy(size);

                        sizeT.rotation.y = Math.PI;
                        sizeT.position.z = this.showCuts
                            ? -(logLen/4 + 1)
                            : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);

                        scene.add(sizeT);

                        sqW = sqW - pattern.items[i].items[j].ws - pattern.items[i].kerf;
                    }

                    usedArea1 += pattern.items[i].items[0].ts + pattern.items[i].kerf;
                } else {
                    let boardWane = pattern.items[i].waneLen ? pattern.items[i].waneLen*1000 : 0;
                    let boardLen = boardWane && boardWane > 0 ? pattern.items[i].len*1000 - boardWane : pattern.items[i].len*1000;
                    let logLen = log.length*1000;

                    geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardLen/2);

                    this.models.push(new Mesh(geometryBoard, materialBoard));
                    let board = this.models[this.models.length - 1];

                    scene.add(board);

                    board.position.z = logLen/4 - boardLen/4;

                    if (order == 1) {
                        board.position.x = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                    } else {
                        board.position.y = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                        board.rotation.z = Math.PI /2;
                    }

                    this.addEdges(board);

                    if (boardWane && boardWane > 0) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardWane));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);

                        if (order == 1) {
                            board.position.x = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                        } else {
                            board.position.y = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                            board.rotation.z = Math.PI /2;
                        }

                        this.addEdges(board);
                    }

                    if (boardLen + 0.001 < logLen && this.showCuts) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, logLen/2 - boardLen/2 - boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardCut));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);

                        if (order == 1) {
                            board.position.x = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                        } else {
                            board.position.y = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                            board.rotation.z = Math.PI /2;
                        }

                        this.addEdges(board);
                    }

                    size = new Text();

                    scene.add(size);

                    size.maxWidth = pattern.items[i].wn;
                    size.text = `${pattern.items[i].tn} x ${pattern.items[i].wn} x ${pattern.items[i].len.toFixed(1)}`;
                    if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].waneLen.toFixed(1) + ")";}
                    size.fontSize = this.calcFontSize(pattern.items[i].ts, pattern.items[i].ws);
                    size.anchorX  = "50%";
                    size.anchorY  = "50%";
                    size.color    = 0x000000;

                    size.position.z = logLen/4 + 1;

                    if (order == 1) {
                        size.position.x = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                        size.rotation.z = Math.PI /2;
                    } else {
                        size.position.y = sq + usedArea1 + pattern.items[i].ts/2 + pattern.kerf;
                    }

                    sizeT = new Text();

                    sizeT.copy(size);

                    sizeT.rotation.y = Math.PI;
                    sizeT.position.z = this.showCuts
                        ? -(logLen/4 + 1)
                        : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);

                    scene.add(sizeT);

                    usedArea1 += pattern.items[i].ts + pattern.kerf;
                }
            }
            //Отрисовка второй, правой половины
            for (let i = cenOrder + 1; i < col; ++i) {
                if (pattern.items[i].hasOwnProperty("items")) {
                    sqW = 0;
                    for (let j = 0; j < pattern.items[i].items.length; j++) {
                        sqW += pattern.items[i].items[j].ws;
                    }
                    sqW += (pattern.items[i].items.length - 1) * pattern.items[i].kerf;
                    sqW /= 2;
                    // let containers = pattern.items[i].items.length;

                    for (let j = 0; j < pattern.items[i].items.length; j++) {
                        let boardWane = pattern.items[i].items[j].waneLen ? pattern.items[i].items[j].waneLen*1000 : 0;
                        let boardLen = boardWane && boardWane > 0 ? pattern.items[i].items[j].len*1000 - boardWane : pattern.items[i].items[j].len*1000;
                        let logLen = log.length*1000;
                        geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardLen/2);

                        this.models.push(new Mesh(geometryBoard, materialBoard));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        if (order == 1) {
                            board.position.x = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                            board.position.y = sqW - pattern.items[i].items[j].ws/2;
                        } else {
                            board.position.y = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                            board.rotation.z = Math.PI /2;
                            board.position.x = sqW - pattern.items[i].items[j].ws/2;
                        }

                        board.position.z = logLen/4 - boardLen/4;

                        this.addEdges(board);

                        if (boardWane && boardWane > 0) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardWane));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);

                            if (order == 1) {
                                board.position.x = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                                board.position.y = sqW - pattern.items[i].items[j].ws/2;
                            } else {
                                board.position.y = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                                board.rotation.z = Math.PI /2;
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            this.addEdges(board);
                        }

                        if (boardLen < logLen && this.showCuts) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, logLen/2 - boardLen/2 - boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardCut));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);

                            if (order == 1) {
                                board.position.x = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                                board.position.y = sqW - pattern.items[i].items[j].ws/2;
                            } else {
                                board.position.y = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                                board.rotation.z = Math.PI /2;
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            this.addEdges(board);
                        }

                        size = new Text();

                        scene.add(size);

                        size.maxWidth = pattern.items[i].items[j].wn;
                        size.text = `${pattern.items[i].items[j].tn} x ${pattern.items[i].items[j].wn} x ${pattern.items[i].items[j].len.toFixed(1)}`;
                        if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].items[j].waneLen.toFixed(1) + ")";}
                        size.fontSize = this.calcFontSize(pattern.items[i].items[j].ts, pattern.items[i].items[j].ws);
                        size.anchorX  = "50%";
                        size.anchorY  = "50%";
                        size.color    = 0x000000;

                        size.position.z = logLen/4 + 1;

                        if (order == 1) {
                            size.position.x = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                            size.rotation.z = Math.PI /2;
                            size.position.y = sqW - pattern.items[i].items[j].ws/2;
                        } else {
                            size.position.y = -(sq + usedArea2 + pattern.items[i].items[j].ts/2 + pattern.items[i].kerf);
                            size.position.x = sqW - pattern.items[i].items[j].ws/2;
                        }

                        sizeT = new Text();

                        sizeT.copy(size);

                        sizeT.rotation.y = Math.PI;
                        sizeT.position.z = this.showCuts
                            ? -(logLen/4 + 1)
                            : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);

                        scene.add(sizeT);

                        sqW = sqW - pattern.items[i].items[j].ws - pattern.items[i].kerf;
                    }

                    usedArea2 += pattern.items[i].items[0].ts + pattern.items[i].kerf;
                } else {
                    let boardWane = pattern.items[i].waneLen ? pattern.items[i].waneLen*1000 : 0;
                    let boardLen = boardWane && boardWane > 0 ? pattern.items[i].len*1000 - boardWane : pattern.items[i].len*1000;
                    let logLen = log.length*1000;
                    geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardLen/2);

                    this.models.push(new Mesh(geometryBoard, materialBoard));
                    let board = this.models[this.models.length - 1];

                    scene.add(board);

                    board.position.z = logLen/4 - boardLen/4;

                    if (order == 1) {
                        board.position.x = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                    } else {
                        board.position.y = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                        board.rotation.z = Math.PI /2;
                    }

                    this.addEdges(board);

                    if (boardWane && boardWane > 0) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardWane));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);

                        if (order == 1) {
                            board.position.x = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                        } else {
                            board.position.y = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                            board.rotation.z = Math.PI /2;
                        }

                        this.addEdges(board);
                    }

                    if (boardLen + 0.001 < logLen && this.showCuts) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, logLen/2 - boardLen/2 - boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardCut));
                        let board = this.models[this.models.length - 1];

                        this.models.push(board);

                        scene.add(board);

                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);

                        if (order == 1) {
                            board.position.x = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                        } else {
                            board.position.y = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                            board.rotation.z = Math.PI /2;
                        }

                        this.addEdges(board);
                    }

                    size = new Text();

                    scene.add(size);

                    size.maxWidth = pattern.items[i].wn;
                    size.text = `${pattern.items[i].tn} x ${pattern.items[i].wn} x ${pattern.items[i].len.toFixed(1)}`;
                    if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].waneLen.toFixed(1) + ")";}
                    size.fontSize = this.calcFontSize(pattern.items[i].ts, pattern.items[i].ws);
                    size.anchorX  = "50%";
                    size.anchorY  = "50%";
                    size.color    = 0x000000;

                    size.position.z = logLen/4 + 1;

                    if (order == 1) {
                        size.position.x = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                        size.rotation.z = Math.PI /2;
                    } else {
                        size.position.y = -(sq + usedArea2 + pattern.items[i].ts/2 + pattern.kerf);
                    }

                    sizeT = new Text();

                    sizeT.copy(size);

                    sizeT.rotation.y = Math.PI;
                    sizeT.position.z = this.showCuts
                        ? -(logLen/4 + 1)
                        : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);

                    scene.add(sizeT);

                    usedArea2 += pattern.items[i].ts + pattern.kerf;
                }
            }
        },

        /**
         * Отрисовка центральных досок
         *
         * @param {object} scene   Сцена
         * @param {object} pattern Параметры постава
         * @param {object} log     Параметры бревна
         */
        drawCenter(scene, pattern, log) {
            let geometryBoard;
            let size, sizeT;
            const materialBoard  = new MeshLambertMaterial({ color: 0xebc81a });
            const materialBoardCut = new MeshLambertMaterial({ color: 0xe88358 });
            const materialBoardWane = new MeshLambertMaterial({ color: 0xFFECB3 });
            let col = pattern.items.length;
            let sqT = 0,
                sqW = 0;

            for (let i=0; i < col; i++){
                if (pattern.items[i].hasOwnProperty("items")) {
                    sqT += pattern.items[i].items[0].ts;
                    if (i != col-1) {
                        sqT += pattern.items[i].kerf;
                    }
                    if (sqW == 0) {
                        for (let j = 0; j < pattern.items[i].items.length; j++) {
                            sqW += pattern.items[i].items[j].ws;
                        }
                        sqW += (pattern.items[i].items.length - 1) * pattern.items[i].kerf;
                    }
                } else {
                    sqT += pattern.items[i].ts;
                    if (i != col-1) {
                        sqT += pattern.kerf;
                    }
                    if (sqW == 0) {
                        sqW = pattern.items[0].ws;
                    }
                }
            }

            sqT /= 2;
            sqW /= 2;

            let centerAreaT = sqT;
            let centerAreaW = sqW;

            for (let i = 0; i < col; i++) {
                if (pattern.items[i].hasOwnProperty("items")) {
                    sqW = centerAreaW;
                    let containers = pattern.items[i].items.length;
                    for (let j = 0; j < pattern.items[i].items.length; j++) {
                        let boardWane = pattern.items[i].items[j].waneLen ? pattern.items[i].items[j].waneLen*1000 : 0;
                        let boardLen = boardWane && boardWane > 0 ? pattern.items[i].items[j].len*1000 - boardWane : pattern.items[i].items[j].len*1000;
                        let logLen = log.length*1000;

                        geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardLen/2);

                        this.models.push(new Mesh(geometryBoard, materialBoard));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.y = sqT - pattern.items[i].items[j].ts/2;
                        board.position.x = sqW - pattern.items[i].items[j].ws/2;

                        board.position.z = logLen/4 - boardLen/4;
                        board.rotation.z = Math.PI /2;

                        this.addEdges(board);

                        if (boardWane && boardWane > 0) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardWane));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);
                            board.rotation.z = Math.PI /2;

                            board.position.y = sqT - pattern.items[i].items[j].ts/2;

                            if (j+1 == Math.round(containers/2) && containers%2 != 0) {
                                board.position.x = 0;
                            } else {
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            this.addEdges(board);
                        }

                        if (boardLen < logLen && this.showCuts) {
                            geometryBoard = new BoxGeometry (pattern.items[i].items[j].ts, pattern.items[i].items[j].ws, logLen/2 - boardLen/2 - boardWane/2);

                            this.models.push(new Mesh(geometryBoard, materialBoardCut));
                            let board = this.models[this.models.length - 1];

                            scene.add(board);

                            board.position.y = sqT - pattern.items[i].items[j].ts/2;

                            if (j+1 == Math.round(containers/2) && containers%2 != 0) {
                                board.position.x = 0;
                            } else {
                                board.position.x = sqW - pattern.items[i].items[j].ws/2;
                            }

                            board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);
                            board.rotation.z = Math.PI /2;

                            this.addEdges(board);
                        }

                        size = new Text();

                        scene.add(size);

                        size.maxWidth = pattern.items[i].items[j].wn;
                        size.text = `${pattern.items[i].items[j].tn} x ${pattern.items[i].items[j].wn} x ${pattern.items[i].items[j].len.toFixed(1)}`;
                        if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].items[j].waneLen.toFixed(1) + ")";}
                        size.fontSize = this.calcFontSize(pattern.items[i].items[j].tn, pattern.items[i].items[j].wn);
                        size.anchorX  = "50%";
                        size.anchorY  = "50%";
                        size.position.y = sqT - pattern.items[i].items[j].ts/2;

                        if (j+1 == Math.round(containers/2) && containers%2 != 0) {
                            size.position.x = 0;
                        } else {
                            size.position.x = sqW - pattern.items[i].items[j].ws/2;
                        }

                        size.position.z = logLen/4 + 1;
                        size.color = 0x000000;

                        sizeT = new Text();

                        sizeT.copy(size);

                        sizeT.rotation.y = Math.PI;
                        sizeT.position.z = this.showCuts
                            ? -(logLen/4 + 1)
                            : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);

                        scene.add(sizeT);

                        sqW = sqW - pattern.items[i].items[j].ws - pattern.items[i].kerf;
                    }

                    sqT = sqT - pattern.items[i].items[0].ts - pattern.items[i].kerf;
                } else {
                    let boardWane = pattern.items[i].waneLen ? pattern.items[i].waneLen*1000 : 0;
                    let boardLen = boardWane && boardWane > 0 ? pattern.items[i].len*1000 - boardWane : pattern.items[i].len*1000;
                    let logLen = log.length*1000;

                    geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardLen/2);

                    this.models.push(new Mesh(geometryBoard, materialBoard));
                    let board = this.models[this.models.length - 1];

                    scene.add(board);

                    board.position.y = sqT - pattern.items[i].ts/2;
                    board.position.z = logLen/4 - boardLen/4;
                    board.rotation.z = Math.PI /2;

                    this.addEdges(board);

                    if (boardWane && boardWane > 0) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardWane));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.y = sqT - pattern.items[i].ts/2;
                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4 - (logLen - boardLen)/4);
                        board.rotation.z = Math.PI /2;

                        this.addEdges(board);
                    }

                    if (boardLen + 0.001 < logLen && this.showCuts) {
                        geometryBoard = new BoxGeometry (pattern.items[i].ts, pattern.items[i].ws, logLen/2 - boardLen/2 - boardWane/2);

                        this.models.push(new Mesh(geometryBoard, materialBoardCut));
                        let board = this.models[this.models.length - 1];

                        scene.add(board);

                        board.position.y = sqT - pattern.items[i].ts/2;
                        board.position.z = -(logLen/4 - (logLen - boardLen - boardWane)/4);
                        board.rotation.z = Math.PI /2;

                        this.addEdges(board);
                    }

                    size = new Text();

                    scene.add(size);

                    size.maxWidth = pattern.items[i].wn;
                    size.text = `${pattern.items[i].tn} x ${pattern.items[i].wn} x ${pattern.items[i].len.toFixed(1)}`;
                    if (boardWane && boardWane > 0) {size.text += "(" + pattern.items[i].waneLen.toFixed(1) + ")";}
                    size.fontSize = this.calcFontSize(pattern.items[i].tn, pattern.items[i].wn);
                    size.anchorX = "50%";
                    size.anchorY = "50%";
                    size.position.y = sqT - pattern.items[i].ts/2;
                    size.position.z = logLen/4 + 1;
                    size.color = 0x000000;

                    sizeT = new Text();

                    sizeT.copy(size);

                    sizeT.rotation.y = Math.PI;
                    sizeT.position.z = this.showCuts
                        ? -(logLen/4 + 1)
                        : -(logLen/4 - (logLen - boardLen - boardWane)/2 + 1);


                    scene.add(sizeT);

                    sqT = sqT - pattern.items[i].ts - pattern.kerf;
                }
            }

            return [centerAreaT, centerAreaW * 2];
        },

        /**
         * Инициализация
         */
        initThree() {
            let width  = window.innerWidth;
            let height = window.innerHeight;
            const toolbarHeight = 64;
            const canvas = document.querySelector("#c");

            canvas.setAttribute("width", width);
            canvas.setAttribute("height", height-toolbarHeight);
            this.renderer = new WebGLRenderer({canvas});
            this.renderer.setClearColor(0xaaaaaa); //renderer.setClearColor(0xeae3e3);

            this.scene  = new Scene();
            this.camera = new PerspectiveCamera(45, width/height, 0.1, 5000);

            if (this.cameraPosition.z == 0) {
                this.camera.position.set(0, 0, this.initialPosition.camera.z);
            } else {
                this.camera.position.set(this.cameraPosition.x, this.cameraPosition.y, this.cameraPosition.z);
            }

            this.controls = new OrbitControls(this.camera, canvas);
            this.controls.target.set(0, 5, 0);
            this.controls.update();

            const skyColor    = 0xffffff;
            const groundColor = 0x555555;
            const intensity   = 1;
            const light = new HemisphereLight(skyColor, groundColor, intensity);
            this.scene.add(light);

            let centArea;
            let cenOrder1 = 0,
                cenOrder2 = 0;

            if (this.pattern.order == 1) {
                cenOrder1 = this.findContainerCenter(this.pattern);
                if (this.pattern.items[cenOrder1].order == 2) {
                    cenOrder2 = this.findContainerCenter(this.pattern.items[cenOrder1]);
                    centArea  = this.drawCenter(this.scene, this.pattern.items[cenOrder1].items[cenOrder2], this.logInfo);
                    this.drawSideBoard(this.scene, this.pattern.items[cenOrder1], 2, cenOrder2, this.logInfo, centArea[0]);
                    this.drawSideBoard(this.scene, this.pattern, 1, cenOrder1, this.logInfo, centArea[1]/2);
                } else {
                    centArea = this.drawCenter(this.scene, this.pattern.items[cenOrder1], this.logInfo);
                    this.drawSideBoard(this.scene, this.pattern, 1, cenOrder1, this.logInfo, centArea[1]/2);
                }
            }
            if (this.pattern.order == 2) {
                cenOrder2 = this.findContainerCenter(this.pattern);
                centArea  = this.drawCenter(this.scene, this.pattern.items[cenOrder2], this.logInfo);
                this.drawSideBoard(this.scene, this.pattern, 2, cenOrder2, this.logInfo, centArea[0]);
            }
            if (this.pattern.order == 0) {
                centArea = this.drawCenter(this.scene, this.pattern, this.logInfo);
            }

            const geometryLog = new CylinderGeometry(this.logInfo.dTop/2, this.logInfo.dBottom/2, (this.logInfo.length*1000)/2 + 1, 64, 256, true, 0);
            const materialLog = new MeshStandardMaterial({
                color: 0x95651f,//0x7f3c3c,
                transparent: true,
                opacity: 0.75, //opacity: this.showLog ? 0.75 : 0,
            });
            materialLog.side = DoubleSide;
            let curvatureProgib = 0;

            if (this.showLog) {
                //const log = new THREE.Mesh(geometryLog, materialLog);
                //this.scene.add(log); //***

                if (this.logInfo.curvature > 0) {
                    //Выравнивание бревна в плоскости
                    let taperStep = (this.logInfo.dBottom/2 - this.logInfo.dTop/2) / 256;
                    let taper = 0;
                    let circle = 0;

                    for (let dot = 0; dot < geometryLog.attributes.position.array.length; dot += 3) {
                        if (this.toggleCurvature) {
                            geometryLog.attributes.position.array[dot+2] += taper;
                        }
                        else {
                            geometryLog.attributes.position.array[dot] += taper;
                        }

                        circle++;

                        if (circle == 64) {
                            taper += taperStep;
                            circle = 0;
                        }
                    }

                    //Кривизна
                    //const rings = 8256/10;
                    curvatureProgib = ((this.logInfo.length * this.logInfo.curvature) / 2) * 10;

                    // this.$debug.log(curvatureProgib);
                    // this.$debug.log(this.logInfo.curvCenter);

                    let curveStep1 = 0;
                    let curveStep2 = 0;
                    curveStep1 = curvatureProgib / (256 * this.logInfo.curvCenter);
                    curveStep2 = curvatureProgib / (256 * (1 - this.logInfo.curvCenter));
                    let curveStep = curvatureProgib / 256;
                    let curve = 0;
                    circle = 0;

                    for (let dot = 0; dot < geometryLog.attributes.position.array.length; dot += 3) {
                        geometryLog.attributes.position.array[dot+2] += curve;
                        circle++;

                        if (circle == 64) {
                            if (dot < geometryLog.attributes.position.array.length * this.logInfo.curvCenter) {
                            curve += curveStep1;
                            }
                            else {
                                curve -= curveStep2;
                            }
                            circle = 0;
                        }
                    }

                    if (!this.toggleCurvature) {
                        curve = 0;
                        circle = 0;
                        for (let dot = 0; dot < geometryLog.attributes.position.array.length; dot += 3) {
                            geometryLog.attributes.position.array[dot+2] -= curve;
                            circle++;

                            if (circle == 64) {
                                curve += curveStep;
                                circle = 0;
                            }
                        }
                    }

                }

                if (this.logInfo.ellipseRatio > 0) {
                    for (let dot = 0; dot < geometryLog.attributes.position.array.length; dot += 3) {
                        if (this.logInfo.rotationAngle == 0) {
                            geometryLog.attributes.position.array[dot] *= (1 - this.logInfo.ellipseRatio/100);
                            geometryLog.attributes.position.array[dot+2] *= (1 + this.logInfo.ellipseRatio/100);
                        }
                        else {
                            geometryLog.attributes.position.array[dot] *= (1 + this.logInfo.ellipseRatio/100);
                            geometryLog.attributes.position.array[dot+2] *= (1 - this.logInfo.ellipseRatio/100);
                        }
                    }
                }

                this.models.push(new Mesh(geometryLog, materialLog));
                const log = this.models[this.models.length - 1];
                this.scene.add(log);

                log.rotation.x = Math.PI*2 * 3/4;
            }

            let sizeLog = new Text();
            this.scene.add(sizeLog);
            sizeLog.text = "D = " + String(this.logInfo.dBottom);
            sizeLog.fontSize = this.logInfo.dBottom > 100 ? 20 : 10;
            sizeLog.anchorX  = "50%";
            sizeLog.anchorY  = "50%";
            sizeLog.position.x = -this.logInfo.dBottom/2;
            sizeLog.position.y = this.logInfo.dBottom/2;
            sizeLog.position.z = this.logInfo.length*1000/4 + 1;
            sizeLog.color = 0x000000;
            sizeLog = new Text();
            this.scene.add(sizeLog);
            sizeLog.text = "D = " + String(this.logInfo.dTop);
            sizeLog.fontSize = this.logInfo.dTop > 100 ? 20 : 10;
            sizeLog.anchorX  = "50%";
            sizeLog.anchorY  = "50%";
            sizeLog.position.x = -this.logInfo.dTop/2;
            sizeLog.position.y = this.logInfo.dTop/2;
            sizeLog.position.z = -this.logInfo.length*1000/4 + 1;
            sizeLog.rotation.y = Math.PI;
            sizeLog.color = 0x000000;
            sizeLog = new Text();
            this.scene.add(sizeLog);
            sizeLog.text = "L = " + String(this.logInfo.length.toFixed(1));
            sizeLog.fontSize = this.logInfo.dTop > 100 ? 24 : 12;
            sizeLog.anchorX  = "50%";
            sizeLog.anchorY  = "50%";
            sizeLog.position.y = this.logInfo.dBottom/2 + curvatureProgib + 5;
            sizeLog.position.y += (this.logInfo.dBottom/2 - this.logInfo.dTop/2);
            sizeLog.rotation.y = Math.PI/2;
            sizeLog.color = 0x000000;
            //***
            const materialRing = new MeshBasicMaterial( { color: 0x5f3f09, side: DoubleSide } ); //75531e
            const geometryTop  = new RingGeometry( this.logInfo.dTop/2, this.logInfo.dTop/2+1, 64 );

            if (this.logInfo.ellipseRatio > 0) {
                for (let dot = 0; dot < geometryTop.attributes.position.array.length; dot += 3) {
                    if (this.logInfo.rotationAngle == 0) {
                        geometryTop.attributes.position.array[dot] *= (1 - this.logInfo.ellipseRatio/100);
                        geometryTop.attributes.position.array[dot+1] *= (1 + this.logInfo.ellipseRatio/100);
                    }
                    else {
                        geometryTop.attributes.position.array[dot] *= (1 + this.logInfo.ellipseRatio/100);
                        geometryTop.attributes.position.array[dot+1] *= (1 - this.logInfo.ellipseRatio/100);
                    }
                }
            }
            //const topRing = new THREE.Mesh( geometryTop, materialTop );
            this.models.push(new Mesh(geometryTop, materialRing));
            const topRing = this.models[this.models.length - 1];

            this.scene.add(topRing);

            topRing.position.z = (-this.logInfo.length*1000)/4 - 0.5;

            const geometryBot = new RingGeometry( this.logInfo.dBottom/2, this.logInfo.dBottom/2+1, 64 );

            if (this.logInfo.ellipseRatio > 0) {
                for (let dot = 0; dot < geometryBot.attributes.position.array.length; dot += 3) {
                    if (this.logInfo.rotationAngle == 0) {
                        geometryBot.attributes.position.array[dot] *= (1 - this.logInfo.ellipseRatio/100);
                        geometryBot.attributes.position.array[dot+1] *= (1 + this.logInfo.ellipseRatio/100);
                    }
                    else {
                        geometryBot.attributes.position.array[dot] *= (1 + this.logInfo.ellipseRatio/100);
                        geometryBot.attributes.position.array[dot+1] *= (1 - this.logInfo.ellipseRatio/100);
                    }
                }
            }
            //const botRing = new THREE.Mesh( geometryBot, materialTop );
            this.models.push(new Mesh(geometryBot, materialRing));
            const botRing = this.models[this.models.length - 1];

            this.scene.add(botRing);

            botRing.position.z = this.logInfo.length*1000/4 + 0.5;

            if (this.logInfo.curvature > 0) {
                if (this.toggleCurvature) {
                    botRing.position.y = this.logInfo.dBottom/2 - this.logInfo.dTop/2;
                }
                else {
                    botRing.position.y -= curvatureProgib;
                    botRing.position.x = this.logInfo.dBottom/2 - this.logInfo.dTop/2;
                }
            }


            requestAnimationFrame(this.render);

            { // Поддержка скриншотов
                const elem = document.querySelector("#screenshot");

                if (!elem.onclick) {
                    elem.onclick = () => {
                        this.render();
                        canvas.toBlob((blob) => {
                            saveBlob(blob, `screencapture-${canvas.width}x${canvas.height}.png`);
                        });
                    }
                }
                /*
                elem.addEventListener("click", () => {
                    render();
                    canvas.toBlob((blob) => {
                        saveBlob(blob, `screencapture-${canvas.width}x${canvas.height}.png`);
                    });
                });
                */

                let component = this;

                const saveBlob = (function () {
                    const a = document.createElement("a");

                    document.body.appendChild(a);
                    a.style.display = "none";

                    return (blob, fileName) => {
                        //const url = window.URL.createObjectURL(blob);
                        const url = component.watermarkedDataURL(canvas, "Saws optimization Web by AVTOMATIKA-VEKTOR");
                        //this.$debug.log("SaveData");
                        a.href     = url;
                        a.download = fileName;
                        a.click();
                        component.alert = component.$t("imageExportCompleted");
                    };
                }());
            }
        },

        /**
         * Отрисовка
         */
        render () {
            if (this.resizeRendererToDisplaySize(this.renderer)) {
                const canvas = this.renderer.domElement;
                this.camera.aspect = canvas.clientWidth / canvas.clientHeight;
                this.camera.updateProjectionMatrix();
            }

            if (this.scene !== undefined && this.camera !== undefined) {
                this.renderer.render(this.scene, this.camera);

                requestAnimationFrame(this.render);
            }

        },

        /**
         * Адаптация визуализатора под размер экрана
         *
         * @param {object} renderer Визуализатор
         *
         * @returns {bool} Флаг изменения размера
         */
        resizeRendererToDisplaySize (renderer) {
            const canvas = renderer.domElement;
            const width  = canvas.clientWidth;
            const height = canvas.clientHeight;
            const needResize = canvas.width !== width || canvas.height !== height;

            if (needResize) {
                renderer.setSize(width, height, false);
            }

            return needResize;
        },

        findContainerCenter (object) {
            const obj = JSON.parse(JSON.stringify(object));
            let n = 0;

            if (obj.order == 1) {
                for (let item1 of obj.items) {
                    if (item1.order == 2) {
                        return n;
                    }
                    n++;
                }
            }

            if (obj.order == 2) {
                for (let item2 of obj.items) {
                    if (item2.order == 0) {
                        return n;
                    }
                    n++;
                }
            }

            return n;
        },

        /**
        * @param {object} canvas
        * @param {string} watermarkText
        */
        watermarkedDataURL(canvas, text) {
            let tempCanvas = document.createElement("canvas");
            let tempCtx    = tempCanvas.getContext("2d");
            let cw, ch;

            this.render();

            cw = tempCanvas.width  = canvas.width;
            ch = tempCanvas.height = canvas.height;
            tempCtx.drawImage(canvas, 0, 0);
            tempCtx.font = "24px verdana";

            /* Watermark */
            let textWidth = tempCtx.measureText(text).width;

            tempCtx.globalAlpha = .5;
            tempCtx.fillStyle   = "white";
            tempCtx.fillText(text, cw - textWidth - 10, ch - 20);
            tempCtx.fillStyle   = "black";
            tempCtx.fillText(text, cw - textWidth - 8, ch - 18);

            /* Информация о бревне */


            return(tempCanvas.toDataURL());
        },

        /**
         * Сохранение позиции камеры
         */
        saveCameraPosition() {
            this.cameraPosition.x = this.camera.position.x;
            this.cameraPosition.y = this.camera.position.y;
            this.cameraPosition.z = this.camera.position.z;
            //this.$debug.log(this.camera.position);
        }
    }
};
</script>

<style lang="sass" scoped>
    #c
        height: 100%
        width: 100%

    .action-button
        position: absolute
        left: 10px
        padding: 10px
        margin: 2px
        color: black
        border: none
        border-radius: 30px
        cursor: pointer
        >
            i
                color: #000
            span
                display: none
                position: relative
                top: -3px
                margin: 0 5px
        &:hover
            background: #fff
            >
                i
                    color: #000
                span
                    display: inline-block
        &:focus
            outline: none

    #screenshot
        top: 70px

    #startPosition
        top: 110px

    #logTransparency
        top: 150px

    #cutsShow
        top: 190px

    #curvatureMode
        top: 230px

    #info
        background: none
        color: #fff
        font-family: Arial, Helvetica, sans-serif
        font-size: 12px
        margin: 10px
        position: absolute
        bottom: 5px

    .info-table
        margin: 10px
        padding: 10px
        position: absolute
        bottom: 5px
        background: rgba(255, 255, 255, .5)
        border: solid 1px #000
        td
            padding: 1px
            color: #000
</style>
